import { Form, Link, usePage } from "@inertiajs/react";
import { useTranslation } from "react-i18next";
import AuthLayout from "@/layouts/auth_layout";
import { Button, buttonVariants } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { PasswordInput } from "@/components/forms/password-input";
import { errorText, FormErrors } from "@/components/forms/form-errors";
import { Label } from "@/components/ui/label";

type Mode =
  | "verification"
  | "confirmation"
  | "password"
  | "session"
  | "reset_request"
  | "reset_password"
  | "recovery";
export default function CustomerAuth({
  mode,
  token,
  sent,
  intake_submitted,
  intake_token,
  continue_path,
}: {
  mode: Mode;
  token?: string;
  sent?: boolean;
  intake_submitted?: boolean;
  intake_token?: string | null;
  continue_path?: string;
}) {
  const { t } = useTranslation();
  const { errors } = usePage().props;
  const title = t(`customer_portal.${mode}_title`);
  function accessPath(path: string) {
    return intake_token == null
      ? path
      : `${path}?${new URLSearchParams({ intake_token }).toString()}`;
  }
  const choosingPassword = mode === "password" || mode === "reset_password";
  const fields = choosingPassword
    ? ["password", "password_confirmation"]
    : mode === "session"
      ? ["email_address", "password"]
      : mode === "verification" || mode === "reset_request"
        ? ["email_address"]
        : [];
  const action =
    mode === "reset_password"
      ? `/customer_portal/password_resets/${encodeURIComponent(token || "")}`
      : mode === "reset_request"
        ? "/customer_portal/password_resets"
        : mode === "confirmation"
          ? "/customer_portal/email_verifications/confirmation"
          : mode === "verification"
            ? "/customer_portal/email_verifications"
            : `/customer_portal/${mode}`;
  return (
    <AuthLayout title={title} description={t("customer_portal.help")}>
      <div className="space-y-4">
        {intake_submitted && (
          <div role="status" className="space-y-2 rounded-lg border p-4">
            <p>{t("website_requests.submitted")}</p>
            <p className="text-muted-foreground text-sm">
              {t("access.optional_password")}
            </p>
          </div>
        )}
        {mode === "recovery" ? (
          <div className="space-y-4">
            <p role="status">{t("customer_portal.recovery_help")}</p>
            <Link
              href={accessPath("/customer_portal/email_verifications/new")}
              className={buttonVariants({ className: "w-full" })}
            >
              {t("customer_portal.recovery_verify")}
            </Link>
          </div>
        ) : sent ? (
          <p role="status">
            {t(
              mode === "reset_request"
                ? "customer_portal.reset_sent"
                : "customer_portal.sent",
            )}
          </p>
        ) : (
          <Form
            key={mode}
            action={action}
            method={mode === "reset_password" ? "patch" : "post"}
            disableWhileProcessing
          >
            {({ processing }) => (
              <div className="space-y-4">
                <FormErrors
                  errors={errors || {}}
                  targets={Object.fromEntries(
                    fields.map((field) => [field, `customer-${field}`]),
                  )}
                />
                {token && <input type="hidden" name="token" value={token} />}
                {intake_token != null && (
                  <input
                    type="hidden"
                    name="intake_token"
                    value={intake_token}
                  />
                )}
                {choosingPassword && (
                  <p className="text-muted-foreground text-sm">
                    {t(
                      mode === "password"
                        ? "customer_portal.password_help"
                        : "customer_portal.reset_help",
                    )}
                  </p>
                )}
                {fields.map((field, index) => {
                  const FieldInput =
                    field === "email_address" ? Input : PasswordInput;
                  return (
                    <div className="space-y-2" key={field}>
                      <Label htmlFor={`customer-${field}`}>
                        {t(`customer_portal.${field}`)}
                      </Label>
                      <FieldInput
                        id={`customer-${field}`}
                        name={field}
                        type={field === "email_address" ? "email" : undefined}
                        required
                        autoFocus={index === 0}
                        minLength={
                          field !== "email_address" && choosingPassword
                            ? 12
                            : undefined
                        }
                        autoComplete={
                          field === "email_address"
                            ? "email"
                            : mode === "session"
                              ? "current-password"
                              : "new-password"
                        }
                        aria-invalid={!!errors?.[field]}
                        aria-describedby={
                          errors?.[field]
                            ? `customer-${field}-error`
                            : undefined
                        }
                      />
                      {errors?.[field] && (
                        <p
                          id={`customer-${field}-error`}
                          className="text-destructive text-sm"
                        >
                          {errorText(errors[field])}
                        </p>
                      )}
                    </div>
                  );
                })}
                <Button
                  type="submit"
                  disabled={processing}
                  aria-busy={processing}
                  className="w-full"
                >
                  {t(`customer_portal.${mode}_submit`)}
                </Button>
              </div>
            )}
          </Form>
        )}
        {intake_submitted && continue_path && (
          <Link className="inline-block underline" href={continue_path}>
            {t("access.set_password_later")}
          </Link>
        )}
        <div className="flex flex-wrap gap-4 text-sm underline">
          {(mode === "session" || mode === "recovery") && (
            <Link href={accessPath("/customer_portal/password_resets/new")}>
              {t("customer_portal.forgot_password")}
            </Link>
          )}
          {mode !== "session" && (
            <Link href={accessPath("/customer_portal/session/new")}>
              {t("customer_portal.session_title")}
            </Link>
          )}
          {mode === "session" && (
            <Link href={accessPath("/customer_portal/email_verifications/new")}>
              {t("customer_portal.verification_title")}
            </Link>
          )}
        </div>
      </div>
    </AuthLayout>
  );
}
